import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CapitalReturn } from '../../data/types';
import { calculateCapitalReturnLines } from '../../domain/lifecycleCalc';
import { addDays, DEMO_TODAY, money, units as formatUnits } from '../../lib/format';
import { newId } from '../../lib/random';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { PageHeader, SectionCard, StatCard, StatGrid } from '../../ui/primitives';

export default function CapitalReturnWizardPage() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const eligible = scope.funds.filter(
    (fund) =>
      fund.basis === 'FIXED_UNITS' && fund.status !== 'WOUND_UP' && fund.status !== 'RAISING',
  );
  const [step, setStep] = useState(0);
  const [fundId, setFundId] = useState(params.get('fund') ?? eligible[0]?.id ?? '');
  const [kind, setKind] = useState<'FINAL' | 'PARTIAL'>('FINAL');
  const [pct, setPct] = useState('100');
  const [income, setIncome] = useState('1.20');
  const [paymentDate, setPaymentDate] = useState(addDays(DEMO_TODAY, 4));
  const [reason, setReason] = useState(
    'Borrower repaid the Riverside loan in full on 14 October 2026, three months early.',
  );
  const unitClass = scope.classes.find((item) => item.fundId === fundId);
  const returnPct = kind === 'FINAL' ? 100 : Math.min(99, Math.max(1, Number(pct) || 0));
  const lines = useMemo(
    () =>
      unitClass
        ? calculateCapitalReturnLines(
            unitClass.id,
            DEMO_TODAY,
            returnPct,
            Number(income) || 0,
            unitClass.unitPrice,
            state.ledger,
            state.holders,
          )
        : [],
    [unitClass, returnPct, income, state.ledger, state.holders],
  );
  const build = (): CapitalReturn => ({
    id: newId('cr'),
    fundId,
    classId: unitClass?.id ?? '',
    kind,
    returnPct,
    reason,
    recordDate: DEMO_TODAY,
    paymentDate,
    finalIncomeCentsPerUnit: Number(income) || 0,
    status: 'DRAFT',
    createdBy: scope.user.name,
    lines,
  });

  return (
    <>
      <PageHeader
        title="New capital return"
        subtitle="Calculated from units on the register today (the record date)."
      />
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
        {['Details', 'Review payments', 'Submit'].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 760 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField
              select
              label="Trust"
              value={fundId}
              onChange={(event) => setFundId(event.target.value)}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            >
              {eligible.map((fund) => (
                <MenuItem key={fund.id} value={fund.id}>
                  {fund.code} · {fund.name}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <ToggleButtonGroup
                exclusive
                value={kind}
                onChange={(_event, next) => next && setKind(next)}
                size="small"
              >
                <ToggleButton value="FINAL">Final return and wind-up</ToggleButton>
                <ToggleButton value="PARTIAL">Partial return</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {kind === 'PARTIAL' && (
              <TextField
                label="Capital to return (%)"
                type="number"
                value={pct}
                onChange={(event) => setPct(event.target.value)}
              />
            )}
            <TextField
              label="Final income (cents per unit)"
              value={income}
              onChange={(event) => setIncome(event.target.value)}
              helperText="Interest accrued to repayment, from the fund accountant"
            />
            <TextField
              label="Payment date"
              type="date"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Reason (shown on the notice)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              multiline
              minRows={2}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={!fundId || !reason.trim() || lines.length === 0}
              onClick={() => setStep(1)}
            >
              Calculate
            </Button>
          </Box>
        </Paper>
      )}
      {step === 1 && (
        <>
          {kind === 'FINAL' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Approving a final return cancels every unit in {scope.fundById.get(fundId)?.code} and
              marks the trust as wound up. Exit statements go to every holder.
            </Alert>
          )}
          <StatGrid>
            <StatCard label="Holders" value={lines.length} />
            <StatCard label="Capital returned" value={money(sumBy(lines, (line) => line.amount))} />
            <StatCard
              label="Final income"
              value={money(sumBy(lines, (line) => line.finalIncome))}
            />
            <StatCard
              label="Units cancelled"
              value={formatUnits(sumBy(lines, (line) => line.unitsCancelled))}
            />
          </StatGrid>
          <SectionCard title="Per-holder payments" noPadding>
            <DataTable
              rows={lines}
              rowKey={(row) => row.holderId}
              dense
              pageSize={15}
              columns={[
                {
                  key: 'holder',
                  label: 'Holder',
                  render: (row) => scope.holderById.get(row.holderId)?.name,
                },
                {
                  key: 'held',
                  label: 'Units held',
                  align: 'right',
                  render: (row) => formatUnits(row.unitsHeld),
                },
                {
                  key: 'cancel',
                  label: 'Units cancelled',
                  align: 'right',
                  render: (row) => formatUnits(row.unitsCancelled),
                },
                {
                  key: 'capital',
                  label: 'Capital',
                  align: 'right',
                  render: (row) => money(row.amount, true),
                },
                {
                  key: 'income',
                  label: 'Final income',
                  align: 'right',
                  render: (row) => money(row.finalIncome, true),
                },
                {
                  key: 'total',
                  label: 'Total paid',
                  align: 'right',
                  render: (row) => <strong>{money(row.amount + row.finalIncome, true)}</strong>,
                },
              ]}
            />
          </SectionCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button variant="contained" onClick={() => setStep(2)}>
              Continue
            </Button>
          </Box>
        </>
      )}
      {step === 2 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 760 }}>
          <Typography variant="h6">Ready to submit</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {money(sumBy(lines, (line) => line.amount + line.finalIncome))} to {lines.length}{' '}
            holders on the payment date. An approver must sign off before the bank file is released.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button
              variant="contained"
              onClick={() => {
                const capitalReturn = build();
                dispatch({ type: 'CAPITAL_RETURN_SAVE', capitalReturn });
                dispatch({ type: 'CAPITAL_RETURN_SUBMIT', id: capitalReturn.id });
                toast('Capital return submitted for approval');
                navigate(`/manager/capital-returns/${capitalReturn.id}`);
              }}
            >
              Submit for approval
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
}
